/**
 * Projects detail page header component.
 */

import { ReactElement, ReactNode, useState } from "react";
import {
  useRoute,
  useLocation,
  useSearch,
  Redirect,
  NeverUnderlineSpace,
  TextLink,
} from "_/components/router";
import { t } from "@lingui/core/macro";

import {
  useDeleteProject,
  hasAttemptEnded,
  Project,
  Attempt,
  useProjectAttempts,
  Revision,
  useUpdateProject,
  Optimization,
  useProjectOptimizations,
} from "_/data/projects";
import { blobDownloadURL } from "_/data/blob";

import { Button } from "_/components/button";
import { Select } from "_/components/select";
import { Icon } from "_/components/icon";
import { Modal } from "_/components/modal";
import { ProjectEditForm } from "_/components/project-forms";
import { type MenuItem, OverlayMenu } from "_/components/overlay-menu";
import { DateTimeRange } from "_/components/date-time";
import { Divider, Tab, TabBar } from "_/components/tab-bar";
import { Tooltip } from "_/components/tooltip";
import { FileDownloadLink } from "_/components/file-download-link";
import { NumberBadge } from "_/components/number-badge";
import {
  projectRoutes,
  projectUrls,
  routeUrls,
  useAnalyzeRoute,
  useOptimizeRoute,
} from "_/routes";

import { Uuid } from "_/types";
import { emailSupport } from "_/utils";
import { dateTimeRangeString, dateTimeString } from "_/utils/datetime";

import * as S from "./styled";
import { PermissionTest, testPermissions } from "_/components/permission-test";
import { useServerFeatureFlag } from "_/data/features";
import { useCurrentOrg } from "_/data/orgs";
import { permissions, useCurrentUserPermissions } from "_/data/rbac";
import { AddToQueueModal } from "_/components/queue/add-modal";
import { OptimizeQueueModal } from "_/components/viewer-controls/optimize";

type PrepareInfoProps = {
  project: Project;
  onRefreshData: () => void;
};

function PrepareInfo({
  project,
  onRefreshData,
}: PrepareInfoProps): ReactElement | null {
  const [_location, setLocation] = useLocation();
  const [_match, params] = useRoute(projectRoutes.prepare);

  const { id: projectId, revisions } = project;
  const revisionIdParam = params?.revisionId;

  // If the revision ID is not provided or is invalid, use the latest revision.
  const revision =
    revisions.find((r) => r.id === revisionIdParam) ?? revisions.at(-1);
  const gcodeBlobRecord = revision?.gcodeFile?.file;

  if (!revision) return null;

  const modifiedDateString = (r: Revision) =>
    t`common.modified ${dateTimeString({
      value: r.updatedAt ?? r.createdAt,
      nowrap: "all", // OK because not copy-able to clipboard
    })}`;

  // Generate revision options for the select component.
  const revOptions = revisions
    .map((r) => {
      // `label` for accessibility, `labelJsx` for usability
      const label = `${t`common.revision-name ${r.number}`} (${modifiedDateString(
        r
      )})`;
      const labelJsx = (
        <S.PickerOptionWrapper>
          <S.PrintOrRevisionNumberInSelect>
            #{r.number}
          </S.PrintOrRevisionNumberInSelect>{" "}
          {modifiedDateString(r)}
        </S.PickerOptionWrapper>
      );

      return { label, labelJsx, value: r.id };
    })
    .toReversed();

  // If the revisionId is not specified, redirect to the latest revision.
  if (!revisionIdParam) {
    return (
      <Redirect
        to={projectUrls.prepare(projectId, revision.id)}
        replace={true}
      />
    );
  }

  return (
    <>
      <S.PickerLabel>{t`common.revision`}</S.PickerLabel>
      <S.RightSectionItems>
        <Select
          options={revOptions}
          value={revisionIdParam}
          onChange={(revisionId: Uuid) => {
            setLocation(projectUrls.prepare(projectId, revisionId));
            onRefreshData();
          }}
          onFocus={onRefreshData}
          isSearchable={false}
        />
        {gcodeBlobRecord && (
          <FileDownloadLink
            href={blobDownloadURL(gcodeBlobRecord.id)}
            title={gcodeBlobRecord.name}
          >
            {t`common.gcode`}
          </FileDownloadLink>
        )}
      </S.RightSectionItems>
    </>
  );
}

type OptimizationInfoProps = {
  /**
   * Project being viewed.
   */
  project: Project;

  /**
   * Optimizations for the current project.
   */
  optimizations?: Optimization[];

  /**
   * Callback to refresh optimizations. Useful while jobs are running.
   */
  onRefreshData: () => void;
};

function OptimizationInfo({
  project,
  optimizations,
  onRefreshData,
}: OptimizationInfoProps): ReactElement | null {
  const optimizeRoute = useOptimizeRoute();
  const optimizationIdParam = optimizeRoute?.optimizationId;
  const [_location, setLocation] = useLocation();

  const projectId = project.id;
  const parentOptimizations = optimizations ?? [];

  const getRedirectUrl = (simId: Uuid) => {
    const opt = parentOptimizations.find((a) => a.job.id === simId);
    if (!opt) throw new Error("Optimization job not found");
    return projectUrls.optimize(projectId, simId);
  };

  if (!parentOptimizations.length) return null;

  const latestRevisionId = project.revisions.at(-1)?.id;

  // Only auto-redirect to an optimization if the latest revision has one.
  // Otherwise let the optimize view show its "not started" state.
  const latestRevisionOptimization = parentOptimizations.find(
    (s) => s.revisionId === latestRevisionId
  );

  if (latestRevisionOptimization && !optimizationIdParam) {
    return (
      <Redirect
        to={getRedirectUrl(latestRevisionOptimization.job.id)}
        replace={true}
      />
    );
  }

  // Generate optimization options for the select component.
  const optimizationOptions = parentOptimizations.map((optimization) => {
    const revision = project.revisions.find(
      (r) => r.id === optimization.revisionId
    );
    if (!revision) throw new Error("Revision not found");

    const optimizationNumber = `#${revision.number}-OP${optimization.number}`;

    const start = optimization.job.createdAt;
    const end = optimization.analysisOutput?.createdAt;

    // `label` for accessibility, `labelJsx` for usability
    const label = `${t`common.optimization-job`} ${optimizationNumber} ${dateTimeRangeString(
      {
        start,
        end,
        nowrap: "all", // OK because not copy-able to clipboard
      }
    )}`;
    const labelJsx = (
      <S.PickerOptionWrapper>
        <S.PrintOrRevisionNumberInSelect>
          {optimizationNumber}
        </S.PrintOrRevisionNumberInSelect>{" "}
        <DateTimeRange start={start} end={end} />
      </S.PickerOptionWrapper>
    );

    return { label, labelJsx, value: optimization.job.id };
  });

  return (
    <>
      <S.PickerLabel>{t`common.optimization-job`}</S.PickerLabel>
      <S.RightSectionItems>
        <Select
          options={optimizationOptions}
          value={optimizationIdParam}
          onChange={(v: Uuid) => setLocation(getRedirectUrl(v))}
          onFocus={onRefreshData}
          isSearchable={false}
        />
      </S.RightSectionItems>
    </>
  );
}

/**
 * Analyze tab, with a menu for choosing between the 3D viewer and the telemetry
 * console.
 *
 * The label navigates to the viewer; only the caret opens the menu. Without
 * `Telemetry::Read`, or with the `telemetry-console` feature flag disabled,
 * no caret is rendered and this is an ordinary tab.
 */
function AnalyzeTab({
  project,
  attempts,
  active,
}: {
  project: Project;
  attempts: Attempt[];
  active: boolean;
}): ReactNode {
  const analyzeRoute = useAnalyzeRoute();
  const search = useSearch();
  const [_location, setLocation] = useLocation();
  const telemetryConsoleFlag = useServerFeatureFlag("telemetry-console");

  const projectId = project.id;

  const tab = (
    <Tab to={projectUrls.analyze(projectId)} active={active}>
      {t`components.project-header.analyze`}
    </Tab>
  );

  // The menu links to a specific attempt, defaulting to the most recent.
  const attemptId = analyzeRoute?.attemptId ?? attempts[0]?.id;
  const attempt = attempts.find((a) => a.id === attemptId);
  const subview = analyzeRoute?.subview ?? "viewer";

  // Records are only bundled into queryable parts once a print ends.
  const telemetryReady = !!attempt && hasAttemptEnded(attempt.status);

  const telemetryLabel = (
    <S.SubviewItem>
      {t`components.project-header.telemetry`}
      {!telemetryReady && (
        <S.SubviewHint>
          {t`components.project-header.telemetry-unavailable`}
        </S.SubviewHint>
      )}
    </S.SubviewItem>
  );

  const items: MenuItem[] = [
    {
      render: t`components.project-header.viewer`,
      icon: subview === "viewer" ? "CheckCircle" : undefined,
      onClick: () => setLocation(projectUrls.analyze(projectId, attemptId)),
    },
    {
      render: telemetryLabel,
      icon: subview === "telemetry" ? "CheckCircle" : undefined,
      disabled: !telemetryReady,
      onClick: () => {
        const url = projectUrls.analyzeTelemetry(projectId, attemptId);
        setLocation(search ? `${url}?${search}` : url);
      },
    },
  ];

  const withMenu = (
    <S.AnalyzeTab>
      {tab}
      <OverlayMenu
        items={items}
        target={
          <S.SubviewCaret
            type="button"
            aria-label={t`components.project-header.analyze-subview`}
          >
            <Icon variant="ArrowDropDown" />
          </S.SubviewCaret>
        }
      />
    </S.AnalyzeTab>
  );

  return (
    <PermissionTest
      requiredPermissions={permissions.telemetry.read}
      render={({ allowed }) =>
        allowed && telemetryConsoleFlag.data && attemptId ? withMenu : tab
      }
    />
  );
}

type AttemptInfoProps = {
  /**
   * Project being viewed.
   */
  project: Project;

  /**
   * Attempts for the current project.
   */
  attempts?: Attempt[];

  /**
   * Callback to refresh data for the attempt. Useful while the print is running.
   */
  onRefreshData: () => void;
};

function AttemptInfo({
  project,
  attempts,
  onRefreshData,
}: AttemptInfoProps): ReactElement | null {
  const analyzeRoute = useAnalyzeRoute();
  const [_location, setLocation] = useLocation();
  const search = useSearch();

  const lastAttemptId = attempts?.[0]?.id;
  const projectId = project.id;
  const attemptIdParam = analyzeRoute?.attemptId;
  const subview = analyzeRoute?.subview ?? "viewer";
  const attempt = attempts?.find((a) => a.id === attemptIdParam);
  const revision = project.revisions.find((r) => r.id === attempt?.revisionId);
  const gcodeBlobRecord = revision?.gcodeFile?.file;

  // Keeps the query being run when switching print jobs on the telemetry
  // subview.
  const withSearch = (url: string) => (search ? `${url}?${search}` : url);

  if (!attempts?.length) return null;

  // Invalid attempt ID. Redirect to the last valid attempt.
  if (lastAttemptId && !attemptIdParam) {
    return (
      <Redirect
        to={projectUrls.analyze(projectId, lastAttemptId)}
        replace={true}
      />
    );
  }

  // A missing attempt id already took the redirect above. `inject` drops an
  // empty optional segment, so defaulting it here would build
  // `/analyze/telemetry`, which parses as an attempt named "telemetry".
  if (!attemptIdParam) return null;

  // Generate attempt options for the select component.
  const attemptOptions = attempts.map((attemptInner) => {
    const revisionInner = project.revisions.find(
      (r) => r.id === attemptInner.revisionId
    );
    if (!revisionInner) throw new Error("Revision not found");

    const revisionAttempts = attempts.filter(
      (a) => a.revisionId === revisionInner.id
    );
    const attemptNumberInRevision =
      revisionAttempts.length - revisionAttempts.indexOf(attemptInner);
    const attemptNumber = `#${revisionInner.number}-${attemptNumberInRevision}`;

    // `label` for accessibility, `labelJsx` for usability
    const label = `${t`common.print-job`} ${attemptNumber} ${dateTimeRangeString(
      {
        start: attemptInner.startedAt,
        end: attemptInner.endedAt,
      }
    )}`;
    const labelJsx = (
      <S.PickerOptionWrapper>
        <S.PrintOrRevisionNumberInSelect>
          {attemptNumber}
        </S.PrintOrRevisionNumberInSelect>{" "}
        <DateTimeRange
          start={attemptInner.startedAt}
          end={attemptInner.endedAt}
        />
      </S.PickerOptionWrapper>
    );

    return { label, labelJsx, value: attemptInner.id };
  });

  return (
    <>
      <S.PickerLabel>{t`common.print-job`}</S.PickerLabel>
      <S.RightSectionItems>
        <Select
          options={attemptOptions}
          value={attemptIdParam}
          onChange={(attemptId: Uuid) => {
            setLocation(
              subview === "telemetry"
                ? withSearch(projectUrls.analyzeTelemetry(projectId, attemptId))
                : projectUrls.analyze(projectId, attemptId)
            );
          }}
          onFocus={onRefreshData}
          isSearchable={false}
        />
        {revision && (
          <TextLink
            variant="muted"
            to={projectUrls.prepare(project.id, revision.id)}
          >
            <S.RevisionWrapper>
              <span>{t`common.revision`}</span>
              <NeverUnderlineSpace />
              <NumberBadge number={revision.number} size="md" />
            </S.RevisionWrapper>
          </TextLink>
        )}
        {gcodeBlobRecord && (
          <FileDownloadLink
            href={blobDownloadURL(gcodeBlobRecord.id)}
            title={gcodeBlobRecord.name}
          >
            G-code
          </FileDownloadLink>
        )}
      </S.RightSectionItems>
    </>
  );
}

/**
 * Modal popup for when the user attempts to delete a project.
 */
function ProjectDeletionModal({
  isOpen,
  onClose,
  project,
}: {
  isOpen: boolean;
  project: Project;
  onClose: () => void;
}) {
  const [_location, setLocation] = useLocation();
  const deleteProject = useDeleteProject();

  async function execDelete() {
    setLocation(routeUrls.projects.index);
    await deleteProject.mutateAsync({ id: project.id });
  }

  const title = t`components.project-card.delete-modal-title`;
  const content = t`components.project-card.delete-modal-content ${project.name}`;

  return (
    <Modal title={title} open={isOpen} onClose={onClose}>
      <p>{content}</p>
      <S.DeleteModalButtons>
        <Button size="small" kind="danger" onClick={() => void execDelete()}>
          {t`common.delete`}
        </Button>
        <Button size="small" kind="secondary" onClick={onClose}>
          {t`common.cancel`}
        </Button>
      </S.DeleteModalButtons>
    </Modal>
  );
}

const ProjectMenu = ({
  project,
  attempts,
  onRefreshData,
}: {
  project: Project;
  attempts: Attempt[];
  onRefreshData: () => void;
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const updateProject = useUpdateProject();
  const { data: currentOrg } = useCurrentOrg();
  const { data: orgPerms } = useCurrentUserPermissions(currentOrg.domainId);
  const canWrite = testPermissions(permissions.projects.write, orgPerms);
  const canDelete = testPermissions(permissions.projects.delete, orgPerms);

  const [isUploading, setIsUploading] = useState(false);

  const archive = () => {
    updateProject.mutate({ ...project, status: "archived" });
  };
  const unarchive = () => {
    updateProject.mutate({ ...project, status: "active" });
  };

  return (
    <>
      <OverlayMenu
        target={
          <Button kind="secondary" size="small" iconOnly>
            <Icon variant="MoreVert" />
          </Button>
        }
        onFocus={onRefreshData}
        placement="bottomStart"
        items={[
          ...(canWrite
            ? [
                {
                  render: t`common.revise`,
                  onClick: () => setShowEditModal(true),
                },
                {
                  render:
                    project.status === "archived"
                      ? t`common.unarchive`
                      : t`common.archive`,
                  onClick: project.status === "archived" ? unarchive : archive,
                },
              ]
            : []),
          {
            render: t`common.support`,
            onClick: () => {
              // Navigate to mailto link for getting project specific support
              emailSupport(`Basis Project Support [${project.id}]`);
            },
          },
          ...(canDelete
            ? [
                {
                  render: () => {
                    // Only show the tooltip if the project cannot be deleted.
                    //
                    // If this pattern becomes common throughout the codebase, it may be
                    // desirable to add a `tooltip` prop for menu items to unify the
                    // implementation.
                    return attempts?.length ? (
                      <Tooltip
                        wait={100}
                        delay={100}
                        placement="left"
                        target={t`common.delete`}
                        content={t`components.project-card.no-delete-tooltip`}
                        style={{ maxWidth: "240px" }}
                      />
                    ) : (
                      <span>{t`common.delete`}</span>
                    );
                  },
                  disabled: !!attempts?.length,
                  onClick: () => setShowDeletionModal(true),
                },
              ]
            : []),
        ]}
      />
      <ProjectDeletionModal
        isOpen={showDeletionModal}
        project={project}
        onClose={() => setShowDeletionModal(false)}
      />
      <Modal
        open={showEditModal}
        title={t`components.project-detail.revise-project`}
        onClose={isUploading ? undefined : () => setShowEditModal(false)}
      >
        <ProjectEditForm
          project={project}
          onComplete={() => setShowEditModal(false)}
          isUploading={isUploading}
          setIsUploading={setIsUploading}
        />
      </Modal>
    </>
  );
};

type ProjectHeaderProps = {
  /**
   * Project to display header for.
   */
  project: Project;

  /**
   * Callback to refresh project revisions. Useful if new revisions were added.
   */
  onRefreshData: () => void;
};

export function ProjectHeader({
  project,
  onRefreshData,
}: ProjectHeaderProps): ReactElement {
  const [prepActive, _prepParams] = useRoute(projectRoutes.wildcards.prepare);
  const [optActive, _optParams] = useRoute(projectRoutes.wildcards.optimize);
  const [anaActive, _anaParams] = useRoute(projectRoutes.wildcards.analyze);
  const [histActive, _histParams] = useRoute(projectRoutes.history);
  const [showAddToQueueModal, setShowAddToQueueModal] = useState(false);
  const [showOptimizeQueueModal, setShowOptimizeQueueModal] = useState(false);
  const optimizeRoute = useOptimizeRoute();

  const projectAttemptsResult = useProjectAttempts(project.id);
  const { data: attempts = [] } = projectAttemptsResult;
  const projectOptimizationsResult = useProjectOptimizations(project.id, {
    parentOnly: true,
  });
  const { data: optimizations = [] } = projectOptimizationsResult;

  const currentOpt = optimizeRoute?.optimizationId
    ? optimizations.find((s) => s.job.id === optimizeRoute.optimizationId)
    : optimizations.find((s) => s.revisionId === project.revisions.at(-1)?.id);
  const currentOptRevision = currentOpt
    ? project.revisions.find((r) => r.id === currentOpt.revisionId)
    : undefined;
  const currentOptGcode = currentOpt?.optimizedGcode ?? undefined;
  const showOptimizeModal =
    optActive &&
    currentOpt?.job.status === "completed" &&
    !!currentOptGcode &&
    !!currentOptRevision;

  // The entire component is wrapped in a ThemeProvider to override the set
  // style and always use the dark theme, regardless of the globally set
  // application theme. This can be safely removed if necessary and only
  // affects styles influenced by themes.
  return (
    <S.Wrapper>
      <S.LeftSection>
        <S.Details>
          <S.Meta>
            <S.Title>
              {project.name}
              {project.status === "archived" && (
                <S.TitleStatus>
                  ({t`components.project-header.archived`})
                </S.TitleStatus>
              )}
            </S.Title>
          </S.Meta>
          <S.Actions>
            <ProjectMenu
              project={project}
              attempts={attempts}
              onRefreshData={onRefreshData}
            />
          </S.Actions>
        </S.Details>
        <S.TabRow>
          <TabBar>
            <Tab to={projectUrls.prepare(project.id)} active={prepActive}>
              {t`components.project-header.prepare`}
            </Tab>
            <Tab to={projectUrls.optimize(project.id)} active={optActive}>
              {t`components.project-header.optimize`}
            </Tab>
            <AnalyzeTab
              project={project}
              attempts={attempts}
              active={anaActive}
            />
            <Divider />
            <Tab to={projectUrls.history(project.id)} active={histActive}>
              {t`components.project-header.history`}
            </Tab>
          </TabBar>
          <PermissionTest requiredPermissions={permissions.queue.create}>
            <S.AddToQueueButton
              onClick={() =>
                showOptimizeModal
                  ? setShowOptimizeQueueModal(true)
                  : setShowAddToQueueModal(true)
              }
            >
              {t`components.queue.add-button`}
            </S.AddToQueueButton>
          </PermissionTest>
        </S.TabRow>
      </S.LeftSection>
      <S.RightSection>
        {prepActive && (
          <PrepareInfo project={project} onRefreshData={onRefreshData} />
        )}
        {optActive && (
          <OptimizationInfo
            project={project}
            optimizations={optimizations}
            onRefreshData={() => {
              onRefreshData();
              void projectOptimizationsResult.refetch();
            }}
          />
        )}
        {anaActive && (
          <AttemptInfo
            project={project}
            attempts={attempts}
            onRefreshData={() => {
              onRefreshData();
              void projectAttemptsResult.refetch();
            }}
          />
        )}
      </S.RightSection>
      <AddToQueueModal
        project={project}
        isOpen={showAddToQueueModal}
        onClose={() => setShowAddToQueueModal(false)}
      />
      {showOptimizeModal && currentOpt && currentOptRevision && (
        <OptimizeQueueModal
          project={project}
          optimization={currentOpt}
          revision={currentOptRevision}
          optimizedGcodeRecord={currentOptGcode}
          isOpen={showOptimizeQueueModal}
          onClose={() => setShowOptimizeQueueModal(false)}
        />
      )}
    </S.Wrapper>
  );
}
